import React, { useState, useEffect, useCallback, useRef } from "react";
import { getJson, postJson } from "../../services/http";
import { RefreshButton } from "../common/IconButton";

const POLL_INTERVAL = 2000;

const COMPONENT_COLORS = {
  Auth: "#6f42c1", Tenants: "#0d6efd", Users: "#0088cc", Events: "#198754",
  Communication: "#e67e22", Workflow: "#d4a017", Content: "#c0392b",
  Reporting: "#16a085", Configuration: "#7f8c8d",
};

const STATUS_MAP = {
  passed: { bg: "#0a7c3e", label: "PASSED" }, failed: { bg: "#c0392b", label: "FAILED" },
  skipped: { bg: "#7f8c8d", label: "SKIPPED" }, timedOut: { bg: "#d35400", label: "TIMEOUT" },
  running: { bg: "#2563eb", label: "RUNNING" }, error: { bg: "#c0392b", label: "ERROR" },
};

const ERROR_PATTERNS = [
  { pattern: /TEST_OWNER_USER.*TEST_OWNER_PASS|env vars are required|not configured on the server/i, title: "Test credentials not configured", message: "TEST_OWNER_USER and TEST_OWNER_PASS are not set on the server.", fix: "Add to /var/www/html/on_screen/server/config/.env:\n  TEST_OWNER_USER=your_username\n  TEST_OWNER_PASS=your_password\n  TEST_TENANT_ID=10\nThen: pm2 restart all --update-env" },
  { pattern: /TEST_TENANT_ID/i, title: "Test tenant id not set", message: "TEST_TENANT_ID is missing.", fix: "Add TEST_TENANT_ID=<tenant id> to the server .env, then pm2 restart." },
  { pattern: /ECONNREFUSED|cannot reach|ERR_CONNECTION_REFUSED/i, title: "API not reachable", message: "The test runner could not reach the API on this server.", fix: "Check that the Express server is running on the configured port (TEST_API_URL or PORT)." },
  { pattern: /ETIMEDOUT|ECONNABORTED|timeout/i, title: "Request timed out", message: "An API request did not respond in time.", fix: "Server may be overloaded or unresponsive. Check pm2 logs and DB health." },
  { pattern: /no token in response|requiresTenantSelection/i, title: "Multi-tenant owner login", message: "Login returned multiple tenants; runner could not pick one.", fix: "Pin TEST_TENANT_ID in the server .env to a tenant the test owner belongs to, then pm2 restart." },
  { pattern: /401.*unauthorized|403.*forbidden|invalid credentials/i, title: "Authentication failed", message: "The test credentials were rejected.", fix: "Verify TEST_OWNER_USER and TEST_OWNER_PASS are valid for an owner account." },
  { pattern: /foreign-tenant data leaked/i, title: "Tenant isolation regression", message: "The API returned data for a tenant the test user does not own.", fix: "This is a security regression. Check tenant-scoping middleware on the failing endpoint." },
];

function diagnoseError(stderr, error) {
  const text = `${stderr || ""} ${error || ""}`;
  for (const p of ERROR_PATTERNS) { if (p.pattern.test(text)) return p; }
  return null;
}

/* ── Scoped CSS injected once to override global .card and other rules ── */
const SCOPED_STYLE = `
  .tr-root, .tr-root * {
    color: #222 !important;
    box-sizing: border-box;
  }
  .tr-root .tr-card {
    background: #ffffff !important;
    border: 1px solid #bbb !important;
    border-radius: 8px !important;
    margin-bottom: 14px !important;
    max-width: none !important;
    padding: 0 !important;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
  }
  .tr-root .tr-card-hdr {
    background: #e9ecef !important;
    color: #222 !important;
    font-weight: 700 !important;
    padding: 10px 14px !important;
    border-bottom: 1px solid #bbb !important;
    border-radius: 8px 8px 0 0 !important;
  }
  .tr-root .tr-badge {
    color: #fff !important;
    padding: 2px 10px !important;
    border-radius: 12px !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    display: inline-block !important;
  }
  .tr-root .tr-suite-name {
    color: #222 !important;
    font-weight: 400 !important;
    font-size: 14px !important;
  }
  .tr-root .tr-sub {
    color: #555 !important;
    font-size: 12px !important;
  }
  .tr-root .tr-err-text {
    color: #991111 !important;
    font-family: monospace !important;
    font-size: 13px !important;
    background: #f5f5f5 !important;
    padding: 6px 10px !important;
    border-radius: 4px !important;
    white-space: pre-wrap !important;
    margin-top: 4px !important;
  }
  .tr-root button {
    color: inherit !important;
  }
  /* A bare icon on the page background, not on a light .tr-card */
  .tr-root button.icon-btn {
    color: #fff !important;
  }
  html.theme-light .tr-root button.icon-btn {
    color: #222 !important;
  }
  .tr-root .tr-btn-run {
    background: #0a7c3e !important;
    color: #fff !important;
    border: none !important;
    padding: 10px 0 !important;
    border-radius: 6px !important;
    font-weight: 700 !important;
    font-size: 15px !important;
    width: 100% !important;
    cursor: pointer !important;
  }
  .tr-root .tr-btn-run:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }
  .tr-root input[type="checkbox"] {
    width: 18px !important;
    height: 18px !important;
    accent-color: #2563eb !important;
  }
  .tr-root code {
    color: #2563eb !important;
    background: #eef !important;
    padding: 1px 4px !important;
    border-radius: 3px !important;
  }
  .tr-root pre {
    color: #d4d4d4 !important;
    background: #1e1e1e !important;
  }
`;

export default function TestRunner({ tenantId }) {
  const [authorized, setAuthorized] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [suites, setSuites] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [groupBy, setGroupBy] = useState("component");
  const [activeRun, setActiveRun] = useState(null);
  const [runError, setRunError] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  const [expandedSuites, setExpandedSuites] = useState(new Set());
  const pollRef = useRef(null);

  useEffect(() => { getJson("/api/super-admin/check-access").then(() => setAuthorized(true)).catch(() => setAuthorized(false)); }, []);

  useEffect(() => {
    if (!authorized) return;
    setHealthLoading(true);
    getJson("/api/test-runner/health").then(setHealth).catch((e) => setHealth({ healthy: false, checks: [], error: e.message })).finally(() => setHealthLoading(false));
    getJson("/api/test-runner/suites").then((d) => { setSuites(d.suites || []); setSelected(new Set((d.suites || []).map((s) => s.id))); });
    getJson("/api/test-runner/runs").then((d) => setRunHistory(d.runs || []));
  }, [authorized]);

  const refreshHealth = () => { setHealthLoading(true); getJson("/api/test-runner/health").then(setHealth).catch((e) => setHealth({ healthy: false, checks: [], error: e.message })).finally(() => setHealthLoading(false)); };

  const pollRun = useCallback((runId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const poll = () => { getJson(`/api/test-runner/runs/${runId}`).then((d) => { setActiveRun(d.run); if (d.run.status !== "running") { clearInterval(pollRef.current); pollRef.current = null; setRunError(d.run.status === "error" || d.run.status === "failed" ? diagnoseError(d.run.rawStderr, null) : null); getJson("/api/test-runner/runs").then((h) => setRunHistory(h.runs || [])); } }); };
    poll(); pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleRun = async () => { const ids = [...selected]; if (!ids.length) return; setRunError(null); try { const d = await postJson("/api/test-runner/run", { suiteIds: ids }); if (d.runId) { setActiveRun({ id: d.runId, status: "running", suiteIds: ids, startedAt: new Date().toISOString() }); setExpandedErrors(new Set()); setExpandedSuites(new Set()); pollRun(d.runId); } } catch (e) { const m = e?.response?.data?.error || e?.message || String(e); setRunError(diagnoseError(m, m) || { title: "Failed to start", message: m, fix: "Check server logs." }); } };

  const toggleSuite = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(suites.map((s) => s.id)));
  const selectNone = () => setSelected(new Set());
  const selectGroup = (key) => { const f = groupBy === "component" ? "component" : "type"; const ids = suites.filter((s) => s[f] === key).map((s) => s.id); setSelected((p) => { const n = new Set(p); const a = ids.every((i) => n.has(i)); ids.forEach((i) => (a ? n.delete(i) : n.add(i))); return n; }); };
  const grouped = {}; const gf = groupBy === "component" ? "component" : "type"; for (const s of suites) { const k = s[gf] || "Other"; if (!grouped[k]) grouped[k] = []; grouped[k].push(s); }
  const toggleError = (i) => setExpandedErrors((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const toggleSuiteExpand = (s) => setExpandedSuites((p) => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const loadRun = (id) => { getJson(`/api/test-runner/runs/${id}`).then((d) => { setActiveRun(d.run); setExpandedErrors(new Set()); setExpandedSuites(new Set()); setRunError(d.run.status === "error" || d.run.status === "failed" ? diagnoseError(d.run.rawStderr, null) : null); }); };

  if (!authorized) return (<div className="tr-root"><style>{SCOPED_STYLE}</style><div style={{ padding: 24 }}><div style={{ background: "#fdd", border: "2px solid #c00", borderRadius: 8, padding: 16 }}><strong>Access Denied</strong> — Super admin access required.</div></div></div>);

  const isRunning = activeRun?.status === "running";
  const healthIssues = health?.checks?.filter((c) => !c.ok) || [];
  const hasHealthIssues = !health?.healthy && healthIssues.length > 0;

  return (
    <div className="tr-root" style={{ maxWidth: 1200, margin: "0 auto", padding: 16 }}>
      <style>{SCOPED_STYLE}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 22 }}>API Test Runner</h3>
          <div className="tr-sub" style={{ marginTop: 2 }}>Fast HTTP-level regression checks against the live API</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <RefreshButton onClick={refreshHealth} loading={healthLoading} label="Refresh Health" />
          <button onClick={() => setGroupBy("component")} style={{ padding: "5px 12px", borderRadius: 4, border: "none", background: groupBy === "component" ? "#2563eb" : "#ddd", color: groupBy === "component" ? "#fff" : "#222", cursor: "pointer" }}>By Component</button>
          <button onClick={() => setGroupBy("type")} style={{ padding: "5px 12px", borderRadius: 4, border: "none", background: groupBy === "type" ? "#2563eb" : "#ddd", color: groupBy === "type" ? "#fff" : "#222", cursor: "pointer" }}>By Type</button>
        </div>
      </div>

      {/* Health loading */}
      {healthLoading && !health && <div style={{ background: "#d0e8ff", border: "2px solid #2563eb", borderRadius: 8, padding: 12, marginBottom: 16 }}>Checking test environment...</div>}

      {/* Health issues */}
      {hasHealthIssues && (
        <div style={{ background: "#fff8e1", border: "2px solid #d4a017", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Environment issues detected — fix these before running tests</div>
          {health.checks.map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
              <span style={{ marginRight: 10, fontSize: 18, flexShrink: 0 }}>{c.ok ? "\u2705" : "\u274C"}</span>
              <div>
                <div style={{ fontWeight: 600 }}>{c.label}</div>
                {!c.ok && <div className="tr-err-text">{c.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Health OK */}
      {health && !hasHealthIssues && !healthLoading && (
        <div style={{ background: "#d4edda", border: "2px solid #0a7c3e", borderRadius: 8, padding: 12, marginBottom: 16, fontWeight: 600 }}>
          {"\u2705"} All {health.checks?.length || 0} environment checks passed — ready to run tests.
        </div>
      )}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* LEFT COLUMN */}
        <div style={{ flex: "0 0 340px", minWidth: 280 }}>
          <div className="tr-card">
            <div className="tr-card-hdr" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Test Suites</span>
              <span>
                <button onClick={selectAll} style={{ border: "1px solid #aaa", background: "#fff", padding: "2px 8px", borderRadius: 3, cursor: "pointer", marginRight: 4, fontSize: 12 }}>All</button>
                <button onClick={selectNone} style={{ border: "1px solid #aaa", background: "#fff", padding: "2px 8px", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>None</button>
              </span>
            </div>
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {Object.entries(grouped).map(([gn, items]) => {
                const allC = items.every((s) => selected.has(s.id));
                return (
                  <div key={gn}>
                    <div onClick={() => selectGroup(gn)} style={{ display: "flex", alignItems: "center", padding: "8px 12px", background: "#e9ecef", borderBottom: "1px solid #bbb", cursor: "pointer" }}>
                      <input type="checkbox" checked={allC} readOnly style={{ marginRight: 8 }} />
                      <span className="tr-badge" style={{ background: COMPONENT_COLORS[gn] || "#7f8c8d" }}>{gn}</span>
                      <span className="tr-sub" style={{ marginLeft: "auto" }}>{items.length} suite{items.length !== 1 ? "s" : ""}</span>
                    </div>
                    {items.map((su) => (
                      <div key={su.id} onClick={() => toggleSuite(su.id)} style={{ display: "flex", alignItems: "center", padding: "8px 12px 8px 28px", borderBottom: "1px solid #ddd", cursor: "pointer", background: selected.has(su.id) ? "#e8f0fe" : "#fff" }}>
                        <input type="checkbox" checked={selected.has(su.id)} readOnly style={{ marginRight: 8 }} />
                        <span className="tr-suite-name">{su.name}</span>
                        <span className="tr-sub" style={{ marginLeft: "auto" }}>{su.id}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div style={{ padding: 12, borderTop: "1px solid #bbb" }}>
              <button className="tr-btn-run" onClick={handleRun} disabled={isRunning || selected.size === 0}>
                {isRunning ? `RUNNING ${activeRun.suiteIds?.length || 0} SUITE(S)...` : `\u25B6 RUN ${selected.size} SUITE${selected.size !== 1 ? "S" : ""}`}
              </button>
            </div>
          </div>

          {runHistory.length > 0 && (
            <div className="tr-card">
              <div className="tr-card-hdr">Run History</div>
              <div style={{ maxHeight: 250, overflowY: "auto" }}>
                {runHistory.map((r) => (
                  <div key={r.id} onClick={() => loadRun(r.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #ddd", cursor: "pointer", background: activeRun?.id === r.id ? "#e8f0fe" : "#fff" }}>
                    <div><div style={{ fontSize: 13 }}>{new Date(r.startedAt).toLocaleTimeString()}</div><div className="tr-sub">{r.suiteIds?.length || 0} suites</div></div>
                    <span className="tr-badge" style={{ background: STATUS_MAP[r.status]?.bg || "#7f8c8d" }}>{STATUS_MAP[r.status]?.label || r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="tr-card">
            <div className="tr-card-hdr">CLI / Local</div>
            <div style={{ padding: 12, fontSize: 13 }}>
              <div style={{ marginBottom: 4 }}><code>test.cmd</code> <span className="tr-sub">— double-click on Windows</span></div>
              <div style={{ marginBottom: 4 }}><code>npm test</code> <span className="tr-sub">— same, no pause</span></div>
              <div style={{ marginBottom: 4 }}><code>npm run test:events</code> <span className="tr-sub">— filter by name</span></div>
              <div><code>npm run test:verbose</code> <span className="tr-sub">— print bodies on fail</span></div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ flex: 1, minWidth: 300 }}>
          {runError && (
            <div className="tr-card" style={{ border: "2px solid #c0392b" }}>
              <div style={{ padding: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{"\u274C"} {runError.title}</div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>{runError.message}</div>
                <div style={{ background: "#f5f5f5", border: "1px solid #ccc", borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" }}>
                  <strong style={{ color: "#0a7c3e" }}>How to fix:</strong>{"\n"}{runError.fix}
                </div>
              </div>
            </div>
          )}

          {!activeRun && !runError && (
            <div className="tr-card" style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 48 }}>{"\u25B6"}</div>
              <div style={{ marginTop: 12 }}>Select test suites and click <strong>Run</strong> to start.</div>
              <div className="tr-sub" style={{ marginTop: 4 }}>CLI: <code>test.cmd</code> or <code>npm test</code> from on_screen/</div>
            </div>
          )}

          {activeRun && (
            <>
              <div className="tr-card">
                <div style={{ padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <span className="tr-badge" style={{ background: STATUS_MAP[activeRun.status]?.bg || "#7f8c8d", marginRight: 8 }}>{STATUS_MAP[activeRun.status]?.label || activeRun.status}</span>
                      <strong>Run Results</strong>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {activeRun.finishedAt && <div className="tr-sub">{Math.round((new Date(activeRun.finishedAt) - new Date(activeRun.startedAt)) / 1000)}s total</div>}
                      {activeRun.exitCode != null && activeRun.exitCode !== 0 && <div style={{ fontSize: 13 }}>{"\u274C"} Exit code: {activeRun.exitCode}</div>}
                    </div>
                  </div>
                  {isRunning && <div style={{ height: 4, background: "#ddd", borderRadius: 2, overflow: "hidden", marginBottom: 12 }}><div style={{ width: "100%", height: 4, background: "#2563eb", animation: "tr-pulse 1.5s infinite" }} /></div>}
                  {activeRun.summary && (
                    <div style={{ display: "flex", gap: 8, textAlign: "center" }}>
                      {[{ l: "Total", v: activeRun.summary.total, c: "#222" }, { l: "Passed", v: activeRun.summary.passed, c: "#0a7c3e" }, { l: "Failed", v: activeRun.summary.failed, c: "#c0392b" }, { l: "Skipped", v: activeRun.summary.skipped, c: "#7f8c8d" }, { l: "Duration", v: activeRun.summary.duration ? `${(activeRun.summary.duration / 1000).toFixed(1)}s` : "-", c: "#222" }].map((x) => (
                        <div key={x.l} style={{ flex: 1, border: "1px solid #ccc", borderRadius: 6, padding: 8, background: "#fff" }}>
                          <div style={{ fontSize: 22, fontWeight: 700 }}><span style={{ color: x.c + " !important" }}>{x.v}</span></div>
                          <div className="tr-sub">{x.l}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!isRunning && !activeRun.summary && !activeRun.tests?.length && (
                    <div style={{ background: "#fdd", border: "1px solid #c00", borderRadius: 6, padding: 12, marginTop: 8 }}><strong>No test results received.</strong> {activeRun.rawStderr ? "Check raw output below." : "Playwright may not be installed."}</div>
                  )}
                </div>
              </div>

              {activeRun.tests?.length > 0 && (
                <div className="tr-card">
                  <div className="tr-card-hdr" style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Test Details</span><span className="tr-sub">{activeRun.tests.length} test(s)</span>
                  </div>
                  {renderTests(activeRun.tests)}
                </div>
              )}

              {activeRun.rawStderr && activeRun.status !== "running" && (
                <div className="tr-card">
                  <div className="tr-card-hdr">Raw Output</div>
                  <pre style={{ margin: 0, padding: 16, maxHeight: 400, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", borderRadius: "0 0 8px 8px", fontSize: 12 }}>{activeRun.rawStderr}</pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  function renderTests(tests) {
    const bySuite = {};
    for (const t of tests) { const k = t.suite || "Unknown"; if (!bySuite[k]) bySuite[k] = []; bySuite[k].push(t); }
    return Object.entries(bySuite).map(([sn, st]) => {
      const p = st.filter((t) => t.status === "passed").length;
      const f = st.filter((t) => t.status === "failed" || t.status === "timedOut").length;
      const sk = st.filter((t) => t.status === "skipped").length;
      const exp = expandedSuites.has(sn);
      return (
        <div key={sn}>
          <div onClick={() => toggleSuiteExpand(sn)} style={{ display: "flex", alignItems: "center", padding: "8px 12px", background: f > 0 ? "#fff0f0" : "#f0fff0", borderBottom: "1px solid #ccc", cursor: "pointer" }}>
            <span style={{ marginRight: 8 }}>{exp ? "\u25BC" : "\u25B6"}</span>
            <strong style={{ fontSize: 13, marginRight: 8 }}>{sn}</strong>
            <span className="tr-badge" style={{ background: "#0a7c3e", marginRight: 4 }}>{p}</span>
            {f > 0 && <span className="tr-badge" style={{ background: "#c0392b", marginRight: 4 }}>{f}</span>}
            {sk > 0 && <span className="tr-badge" style={{ background: "#7f8c8d" }}>{sk}</span>}
            <span className="tr-sub" style={{ marginLeft: "auto" }}>{st.length} test{st.length !== 1 ? "s" : ""}</span>
          </div>
          {(exp || f > 0) && st.map((t, i) => {
            const gi = tests.indexOf(t);
            const fail = t.status === "failed" || t.status === "timedOut";
            const ee = expandedErrors.has(gi);
            return (
              <div key={i}>
                <div onClick={() => fail && toggleError(gi)} style={{ display: "flex", alignItems: "center", padding: "6px 12px 6px 36px", borderBottom: "1px solid #eee", background: fail ? "#fff5f5" : "#fff", cursor: fail ? "pointer" : "default" }}>
                  <span style={{ marginRight: 8, fontSize: 16 }}>{t.status === "passed" ? "\u2705" : t.status === "skipped" ? "\u23F8" : "\u274C"}</span>
                  <span style={{ fontSize: 13 }}>{t.title}</span>
                  <span className="tr-sub" style={{ marginLeft: "auto" }}>{t.duration ? `${(t.duration / 1000).toFixed(2)}s` : ""}</span>
                  {fail && <span style={{ marginLeft: 8, fontSize: 12 }}>{ee ? "\u25B2" : "\u25BC"}</span>}
                </div>
                {fail && ee && (
                  <div style={{ padding: "12px 16px 12px 48px", fontSize: 12, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", borderBottom: "1px solid #ccc" }}>
                    {t.error ? (
                      <>
                        <div className="tr-err-text">{t.error}</div>
                        {(() => { const d = diagnoseError("", t.error); return d ? (<div style={{ background: "#e8f5e9", border: "1px solid #0a7c3e", borderRadius: 4, padding: "6px 8px", margin: "8px 0" }}><strong>Likely cause:</strong> {d.title}{"\n"}<strong>Fix:</strong> {d.fix}</div>) : null; })()}
                        {t.errorStack && <div style={{ marginTop: 8 }}>{t.errorStack}</div>}
                      </>
                    ) : <div className="tr-err-text">No error details. Check Raw Output.</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    });
  }
}
